import { useRef, useState } from "react";
import { addPilotComment, type ReviewComments } from "@/api/pilotEditorial";
import { errorMessage } from "@/api/email";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import type { ReviewSelection } from "@/lib/pilotReview";

export function PilotMessageComments({data, selection, slug, campaignId, name, variantLabel, onClose, onAdded}: {
  data: ReviewComments; selection: ReviewSelection; slug: string; campaignId: number; name: string; variantLabel?: string;
  onClose: () => void; onAdded: (data: ReviewComments) => void;
}) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const request = useRef<{id: string; body: string} | null>(null);
  async function add() {
    if (busy || !body.trim() || !data.can_comment) return;
    setBusy(true); setError("");
    if (!request.current || request.current.body !== body.trim()) request.current = {id: crypto.randomUUID(), body: body.trim()};
    try {
      const updated = await addPilotComment(slug, campaignId, selection, request.current.body, data.text_hash, request.current.id);
      onAdded(updated); setBody(""); request.current = null;
    } catch (cause) {setError(errorMessage(cause));}
    finally {setBusy(false);}
  }
  return <Sheet open onOpenChange={(open) => {if (!open && !busy) onClose();}}>
    <SheetContent className="flex w-full flex-col gap-0 sm:max-w-[540px]" onInteractOutside={(e) => {if (busy) e.preventDefault();}} onEscapeKeyDown={(e) => {if (busy) e.preventDefault();}}>
      <SheetHeader className="shrink-0 pb-4 pr-6">
        <SheetTitle>Commentaires</SheetTitle>
        <SheetDescription>{name} · {selection.position === 1 ? `J0 · ${variantLabel ?? (selection.variant === "B" ? "B Neurosud" : "A Courte")}` : ["", "", "J+4", "J+11", "J+21"][selection.position]}</SheetDescription>
      </SheetHeader>
      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        {data.comments.length ? <ol aria-label="Commentaires de cet email" className="space-y-4">
          {data.comments.map((comment) => <li key={comment.id} className="rounded-lg border border-slate-200 p-4">
            <div className="text-sm font-medium text-slate-900">{comment.author}</div>
            <time dateTime={comment.at} className="mt-1 block text-xs text-slate-500">{new Intl.DateTimeFormat("fr-FR",{dateStyle:"short",timeStyle:"medium"}).format(new Date(comment.at))}</time>
            {comment.previous_version && <p className="mt-1 text-xs text-slate-500">Sur une version précédente</p>}
            <p className="mt-3 whitespace-pre-wrap break-words text-sm leading-6 text-slate-800">{comment.body}</p>
          </li>)}
        </ol> : <p className="text-sm text-slate-500">Aucune remarque pour cet email.</p>}
      </div>
      {data.can_comment && <form className="mt-4 shrink-0 border-t border-slate-100 pt-4" onSubmit={(e) => {e.preventDefault(); void add();}}>
        <label className="text-sm font-medium" htmlFor="pilot-comment">Votre commentaire</label>
        <textarea id="pilot-comment" value={body} maxLength={5000} disabled={busy} onChange={(e) => setBody(e.target.value)}
          className="mt-2 h-28 w-full resize-y rounded-lg border border-slate-200 p-3 text-sm leading-6 focus:outline-none focus:ring-1 focus:ring-slate-400"/>
        {error && <p role="alert" className="my-2 text-sm text-rose-800">{error}</p>}
        <div className="mt-2 flex justify-end"><Button type="submit" disabled={busy || !body.trim()}>{busy ? "Ajout…" : "Ajouter"}</Button></div>
      </form>}
    </SheetContent>
  </Sheet>;
}
